import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Check, Truck, MapPin, Calendar, ArrowLeft, Loader2, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';
import { qtyLabel } from '../lib/cartUnits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_DELIVERY_SLOTS = [
    { value: 'morning', label: 'Morning (7:00 AM – 12:00 PM)' },
    { value: 'afternoon', label: 'Afternoon (12:00 PM – 5:00 PM)' },
];

export default function CheckoutPage() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { fetchCart } = useCart();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [fulfilment, setFulfilment] = useState(null);
    const [timeSlot, setTimeSlot] = useState('morning');

    const [deliveryInfo, setDeliveryInfo] = useState({
        address: '',
        suburb: '',
        postcode: '',
        notes: ''
    });

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const response = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
                setOrder(response.data);
                
                // Pre-fill from user data if available
                if (user?.address) {
                    setDeliveryInfo(prev => ({
                        ...prev,
                        address: user.address || '',
                        suburb: user.suburb || '',
                        postcode: user.postcode || ''
                    }));
                }
            } catch (error) {
                toast.error('Order not found');
                navigate('/cart');
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [orderId, navigate, user]);

    useEffect(() => {
        if (!order?.delivery_type) return;
        axios.get(`${API}/fulfilment?delivery_type=${order.delivery_type}`, { withCredentials: true })
            .then(res => setFulfilment(res.data))
            .catch(() => {});
    }, [order?.delivery_type]);

    const handleSubmitOrder = async (e) => {
        e.preventDefault();
        
        if (order?.delivery_type === 'delivery') {
            if (!deliveryInfo.address || !deliveryInfo.suburb || !deliveryInfo.postcode) {
                toast.error('Please fill in all delivery details');
                return;
            }
        }

        setSubmitting(true);
        try {
            // Save delivery info and submit the order (rests at Pending for admin confirmation)
            await axios.put(
                `${API}/orders/${orderId}`,
                {
                    delivery_info: {
                        address: deliveryInfo.address,
                        suburb: deliveryInfo.suburb,
                        postcode: deliveryInfo.postcode,
                        notes: deliveryInfo.notes,
                        time_slot: timeSlot
                    }
                },
                { withCredentials: true }
            );
            
            await fetchCart();
            navigate('/checkout/success?order_id=' + orderId);
        } catch (error) {
            console.error('Error submitting order:', error);
            toast.error('Failed to submit order. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
            </div>
        );
    }

    if (!order) {
        return null;
    }

    const itemsSubtotal = order.subtotal ?? order.items.reduce((sum, i) => sum + (i.price || 0) * i.quantity, 0);
    const deliveryFee = order.delivery_type === 'delivery' ? (order.delivery_fee ?? 15) : 0;
    const orderTotal = order.total ?? (itemsSubtotal + deliveryFee);

    return (
        <div className="min-h-screen bg-[#F8F9FA] pb-8">
            <div className="container-main py-6 md:py-8">
                {/* Back Button */}
                <Link to="/cart" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Cart
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
                            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                                {order.delivery_type === 'delivery' ? (
                                    <>
                                        <Truck className="w-5 h-5 text-[#1B4D3E]" />
                                        Delivery Details
                                    </>
                                ) : (
                                    <>
                                        <MapPin className="w-5 h-5 text-[#1B4D3E]" />
                                        Pickup Details
                                    </>
                                )}
                            </h2>

                            <form onSubmit={handleSubmitOrder} className="space-y-6">
                                {order.delivery_type === 'delivery' && (
                                    <>
                                        <div>
                                            <Label htmlFor="address">Street Address *</Label>
                                            <Input
                                                id="address"
                                                value={deliveryInfo.address}
                                                onChange={(e) => setDeliveryInfo(prev => ({ ...prev, address: e.target.value }))}
                                                placeholder="123 Main Street"
                                                className="mt-1"
                                                data-testid="address-input"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="suburb">Suburb *</Label>
                                                <Input
                                                    id="suburb"
                                                    value={deliveryInfo.suburb}
                                                    onChange={(e) => setDeliveryInfo(prev => ({ ...prev, suburb: e.target.value }))}
                                                    placeholder="Manly"
                                                    className="mt-1"
                                                    data-testid="suburb-input"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="postcode">Postcode *</Label>
                                                <Input
                                                    id="postcode"
                                                    value={deliveryInfo.postcode}
                                                    onChange={(e) => setDeliveryInfo(prev => ({ ...prev, postcode: e.target.value }))}
                                                    placeholder="2095"
                                                    className="mt-1"
                                                    data-testid="postcode-input"
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="fulfilment-card">
                                    <div className="flex items-center gap-2 text-slate-900 font-semibold">
                                        <Calendar className="w-4 h-4 text-[#1B4D3E]" />
                                        {order.delivery_type === 'delivery' ? 'Delivery' : 'Pickup'} Schedule
                                    </div>
                                    {fulfilment ? (
                                        <div className="mt-2 text-sm text-slate-600 space-y-1">
                                            <p data-testid="fulfil-date">
                                                {order.delivery_type === 'delivery' ? 'Delivery date: ' : 'Pickup date: '}
                                                <span className="font-semibold text-slate-900">{fulfilment.fulfil_label}</span>
                                            </p>
                                            {order.delivery_type === 'pickup' && (
                                                <>
                                                    <p>Window: <span className="font-medium text-slate-900">{fulfilment.window_label}</span></p>
                                                    <p className="flex items-start gap-1">
                                                        <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#1B4D3E] shrink-0" />
                                                        <span>{fulfilment.pickup_location?.name} — {fulfilment.pickup_location?.address}</span>
                                                    </p>
                                                </>
                                            )}
                                            <p className="text-xs text-slate-500">Order cut-off: {fulfilment.cutoff_label} (Sydney time)</p>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-400 mt-2">Loading schedule…</p>
                                    )}

                                    {order.delivery_type === 'delivery' && (
                                        <div className="mt-4">
                                            <Label className="text-sm">Preferred delivery time slot</Label>
                                            <div className="grid grid-cols-2 gap-2 mt-1">
                                                {(fulfilment?.slots || DEFAULT_DELIVERY_SLOTS).map((s) => (
                                                    <button
                                                        type="button"
                                                        key={s.value}
                                                        onClick={() => setTimeSlot(s.value)}
                                                        className={`text-sm font-medium py-2 rounded-lg border transition-colors ${timeSlot === s.value ? 'bg-[#1B4D3E] text-white border-[#1B4D3E]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B4D3E]'}`}
                                                        data-testid={`slot-${s.value}`}
                                                    >
                                                        {s.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <Label htmlFor="notes">Additional Notes (optional)</Label>
                                    <Textarea
                                        id="notes"
                                        value={deliveryInfo.notes}
                                        onChange={(e) => setDeliveryInfo(prev => ({ ...prev, notes: e.target.value }))}
                                        placeholder="Leave at front door, ring bell, etc."
                                        className="mt-1"
                                        data-testid="notes-input"
                                    />
                                </div>

                                <div className="bg-slate-50 rounded-lg p-4">
                                    <p className="text-sm text-slate-600">
                                        <strong>Payment:</strong> Cash/EFTPOS on delivery or bank transfer
                                    </p>
                                </div>

                                <Button 
                                    type="submit" 
                                    className="btn-primary w-full py-4 h-auto text-lg"
                                    disabled={submitting}
                                    data-testid="submit-order-btn"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-5 h-5 mr-2" />
                                            Submit Order
                                        </>
                                    )}
                                </Button>
                            </form>
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 sticky top-24">
                            <h2 className="text-lg font-bold text-slate-900 mb-4">Order Summary</h2>
                            
                            <div className="max-h-64 overflow-y-auto space-y-3 mb-4 scrollbar-hide">
                                {order.items.map((item) => (
                                    <div key={item.product_id} className="flex justify-between text-sm">
                                        <span className="text-slate-600">
                                            {qtyLabel(item)} {item.name}
                                        </span>
                                        <span className="text-slate-900 font-medium">
                                            {item.price ? `$${(item.price * item.quantity).toFixed(2)}` : item.unit}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="border-t pt-4 space-y-2">
                                <div className="flex justify-between text-slate-600">
                                    <span>Subtotal</span>
                                    <span className="font-medium text-slate-900">
                                        ${itemsSubtotal.toFixed(2)}
                                    </span>
                                </div>
                                {order.delivery_type === 'delivery' ? (
                                    <div className="flex justify-between text-slate-600" data-testid="summary-delivery-fee">
                                        <span>Delivery fee</span>
                                        <span className="font-medium text-slate-900">${deliveryFee.toFixed(2)}</span>
                                    </div>
                                ) : (
                                    <div className="flex justify-between text-slate-600" data-testid="summary-pickup">
                                        <span>Pickup</span>
                                        <span className="text-[#1B4D3E] font-medium">Orange Grove Market</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-lg font-bold text-slate-900 border-t pt-3">
                                    <span>Total</span>
                                    <span className="text-[#1B4D3E]" data-testid="summary-total">
                                        ${orderTotal.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
